import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { BankProfile, Vehicle } from '@otw/models';
import { useBankProfiles } from '@/hooks/admin/useAdminBankProfiles';
import {
  ExtraLineItemType,
  PdfVehiclePricing,
  RETAIL_ORDER_DOC_FEE,
  SaleDocumentMode,
  SaleInvoiceRecipient,
  SaleInvoiceSubmitData,
} from './saleInvoiceTypes';

export type { SaleInvoiceSubmitData, SaleDocumentMode, SaleInvoiceRecipient };

interface SaleInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  onSubmit: (data: SaleInvoiceSubmitData) => void;
  initialRecipient?: SaleInvoiceRecipient;
  loading?: boolean;
}

interface VehicleBankAssignment {
  vehicleId: string;
  vehicle: Vehicle;
  bankProfile: BankProfile | null;
  isCashDeal: boolean;
  shippingPrice: number;
  shippingPriceInput: string;
}

interface LineItemDraft {
  id: string;
  label: string;
  amountInput: string;
  type: ExtraLineItemType;
}

const createEmptyLineItemDraft = (): LineItemDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  amountInput: '',
  type: 'charge',
});

const signedLineItemAmount = (amount: number, type: ExtraLineItemType) =>
  type === 'deposit' ? -amount : amount;

const SaleInvoiceDialog: React.FC<SaleInvoiceDialogProps> = ({
  open,
  onClose,
  vehicles,
  onSubmit,
  initialRecipient,
  loading = false,
}) => {
  const [assignments, setAssignments] = useState<VehicleBankAssignment[]>([]);
  const [documentMode, setDocumentMode] = useState<SaleDocumentMode>('retail_order');
  const [separateCustomerInvoices, setSeparateCustomerInvoices] = useState(false);
  const [generateOdometerStatements, setGenerateOdometerStatements] = useState(false);
  const [hideVins, setHideVins] = useState(false);
  const [includeDocFee, setIncludeDocFee] = useState(false);
  const [downPaymentAmountInput, setDownPaymentAmountInput] = useState('');
  const [lineItemDrafts, setLineItemDrafts] = useState<LineItemDraft[]>([]);
  const [manualFirstName, setManualFirstName] = useState('');
  const [manualLastName, setManualLastName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualCompany, setManualCompany] = useState('');
  const [manualStreetAddress, setManualStreetAddress] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const [manualPostalCode, setManualPostalCode] = useState('');
  const [pdfPricingByVehicleId, setPdfPricingByVehicleId] = useState<Record<string, PdfVehiclePricing>>({});
  const { bankProfiles, isLoading: isLoadingBankProfiles } = useBankProfiles();
  const salesBankProfiles = useMemo(
    () => (bankProfiles || []).filter(profile => (profile.profileType || 'sales') === 'sales'),
    [bankProfiles]
  );
  const prevOpenRef = useRef(false);
  const vehicleIdsKey = useMemo(() => vehicles.map((vehicle) => vehicle.id).join('|'), [vehicles]);

  const isPdfMode = documentMode === 'invoice_pdf' || documentMode === 'down_payment_pdf';

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;

    if (justOpened) {
      setAssignments(
        vehicles.map(vehicle => ({
          vehicleId: vehicle.id,
          vehicle,
          bankProfile: null,
          isCashDeal: false,
          shippingPrice: 0,
          shippingPriceInput: '',
        }))
      );
      setDocumentMode('retail_order');
      setSeparateCustomerInvoices(false);
      setGenerateOdometerStatements(false);
      setHideVins(false);
      setIncludeDocFee(false);
      setDownPaymentAmountInput('');
      setLineItemDrafts([]);
      setManualFirstName(initialRecipient?.first_name || '');
      setManualLastName(initialRecipient?.last_name || '');
      setManualEmail(initialRecipient?.email || '');
      setManualCompany(initialRecipient?.company || '');
      setManualStreetAddress(initialRecipient?.street_address || '');
      setManualCity(initialRecipient?.city || '');
      setManualState(initialRecipient?.state || '');
      setManualPostalCode(initialRecipient?.postal_code || '');
      setPdfPricingByVehicleId(
        vehicles.reduce((acc, vehicle) => {
          acc[vehicle.id] = {
            listPrice: Number(vehicle.optic_list_price || 0),
            shippingPrice: 0,
          };
          return acc;
        }, {} as Record<string, PdfVehiclePricing>)
      );
    }
  }, [open, vehicles, initialRecipient]);

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }

    prevOpenRef.current = true;

    setAssignments((prev) => {
      const prevById = new Map(prev.map((assignment) => [assignment.vehicleId, assignment]));
      const next = vehicles.map((vehicle) => {
        const existing = prevById.get(vehicle.id);
        if (existing) {
          return {
            ...existing,
            vehicle,
          };
        }
        return {
          vehicleId: vehicle.id,
          vehicle,
          bankProfile: null,
          isCashDeal: false,
          shippingPrice: 0,
          shippingPriceInput: '',
        };
      });

      const sameOrderAndValues =
        prev.length === next.length &&
        prev.every((assignment, idx) => {
          const candidate = next[idx];
          return (
            assignment.vehicleId === candidate.vehicleId &&
            assignment.bankProfile?.id === candidate.bankProfile?.id &&
            assignment.isCashDeal === candidate.isCashDeal &&
            assignment.shippingPrice === candidate.shippingPrice &&
            assignment.shippingPriceInput === candidate.shippingPriceInput &&
            assignment.vehicle === candidate.vehicle
          );
        });

      return sameOrderAndValues ? prev : next;
    });

    setPdfPricingByVehicleId((prev) => {
      const next: Record<string, PdfVehiclePricing> = {};
      vehicles.forEach((vehicle) => {
        next[vehicle.id] = {
          listPrice: prev[vehicle.id]?.listPrice ?? Number(vehicle.optic_list_price || 0),
          shippingPrice: prev[vehicle.id]?.shippingPrice ?? 0,
        };
      });
      return next;
    });
  }, [open, vehicleIdsKey, vehicles]);

  const groupedByBank = useMemo(() => {
    const groups = new Map<string, VehicleBankAssignment[]>();

    assignments.forEach(assignment => {
      if (assignment.bankProfile && !assignment.isCashDeal) {
        const bankId = assignment.bankProfile.id;
        if (!groups.has(bankId)) {
          groups.set(bankId, []);
        }
        groups.get(bankId)!.push(assignment);
      }
    });

    return Array.from(groups.entries()).map(([, vehicleAssignments]) => {
      const bankProfile = vehicleAssignments[0].bankProfile!;
      return {
        bankProfile,
        vehicleIds: vehicleAssignments.map(a => a.vehicleId),
        vehicles: vehicleAssignments.map(a => a.vehicle),
      };
    });
  }, [assignments]);

  const cashDealVehicles = useMemo(() => {
    return assignments.filter(a => a.isCashDeal).map(a => a.vehicleId);
  }, [assignments]);

  const handleBankChange = (vehicleId: string, bankProfile: BankProfile | null) => {
    setAssignments(prev =>
      prev.map(assignment =>
        assignment.vehicleId === vehicleId
          ? { ...assignment, bankProfile, isCashDeal: false }
          : assignment
      )
    );
  };

  const handleCashDealChange = (vehicleId: string, isCashDeal: boolean) => {
    setAssignments(prev =>
      prev.map(assignment =>
        assignment.vehicleId === vehicleId
          ? { ...assignment, isCashDeal, bankProfile: isCashDeal ? null : assignment.bankProfile }
          : assignment
      )
    );
  };

  const handleShippingPriceChange = (vehicleId: string, shippingPriceInput: string) => {
    const parsedPrice = parseFloat(shippingPriceInput);
    const shippingPrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
    setAssignments(prev =>
      prev.map(assignment =>
        assignment.vehicleId === vehicleId
          ? { ...assignment, shippingPrice, shippingPriceInput }
          : assignment
      )
    );
    setPdfPricingByVehicleId(prev => ({
      ...prev,
      [vehicleId]: {
        listPrice: prev[vehicleId]?.listPrice ?? Number(vehicles.find(v => v.id === vehicleId)?.optic_list_price || 0),
        shippingPrice,
      },
    }));
  };

  const handlePdfListPriceChange = (vehicleId: string, listPrice: number) => {
    setPdfPricingByVehicleId(prev => ({
      ...prev,
      [vehicleId]: {
        listPrice,
        shippingPrice: prev[vehicleId]?.shippingPrice ?? 0,
      },
    }));
  };

  const handlePdfShippingChange = (vehicleId: string, shippingPrice: number) => {
    setPdfPricingByVehicleId(prev => ({
      ...prev,
      [vehicleId]: {
        listPrice: prev[vehicleId]?.listPrice ?? Number(vehicles.find(v => v.id === vehicleId)?.optic_list_price || 0),
        shippingPrice,
      },
    }));
  };

  const handleSubmit = () => {
    const bankAssignments = groupedByBank.map(group => ({
      vehicleIds: group.vehicleIds,
      bankProfile: group.bankProfile,
    }));

    const allVehicleIds = vehicles.map(v => v.id);

    const shippingPrices: Record<string, number> = {};
    if (documentMode === 'invoice_pdf') {
      vehicles.forEach(vehicle => {
        shippingPrices[vehicle.id] = pdfPricingByVehicleId[vehicle.id]?.shippingPrice ?? 0;
      });
    } else if (documentMode === 'retail_order') {
      assignments.forEach(assignment => {
        shippingPrices[assignment.vehicleId] = assignment.shippingPrice || 0;
      });
    }

    const parsedDownPayment = parseFloat(downPaymentAmountInput);
    const extraLineItems = lineItemDrafts.flatMap((draft) => {
      const label = draft.label.trim();
      const amount = parseFloat(draft.amountInput);
      if (label === '' && draft.amountInput.trim() === '') {
        return [];
      }
      if (label !== '' && Number.isFinite(amount) && amount > 0) {
        return [{ label, amount, type: draft.type }];
      }
      return [];
    });

    onSubmit({
      mode: documentMode,
      bankAssignments,
      cashDealVehicleIds: cashDealVehicles,
      allVehicleIds,
      separateCustomerInvoices,
      generateOdometerStatements,
      hideVins,
      includeDocFee: documentMode === 'invoice_pdf' && includeDocFee,
      shippingPrices,
      extraLineItems,
      recipient: {
        email: manualEmail.trim(),
        first_name: manualFirstName.trim(),
        last_name: manualLastName.trim(),
        company: manualCompany.trim() || undefined,
        street_address: manualStreetAddress.trim() || undefined,
        city: manualCity.trim() || undefined,
        state: manualState.trim() || undefined,
        postal_code: manualPostalCode.trim() || undefined,
      },
      pdfPricingByVehicleId,
      downPaymentAmount: Number.isFinite(parsedDownPayment) ? parsedDownPayment : 0,
    });
  };

  const allVehiclesAssigned = assignments.every(a => a.bankProfile !== null || a.isCashDeal);
  const hasAssignments = groupedByBank.length > 0 || cashDealVehicles.length > 0;
  const hasClientInfo =
    manualFirstName.trim() !== '' &&
    manualLastName.trim() !== '' &&
    manualEmail.trim() !== '' &&
    manualCompany.trim() !== '' &&
    manualStreetAddress.trim() !== '' &&
    manualCity.trim() !== '' &&
    manualState.trim() !== '' &&
    manualPostalCode.trim() !== '';
  const hasPdfPricingValues = vehicles.every(vehicle => {
    const pricing = pdfPricingByVehicleId[vehicle.id];
    return pricing && pricing.listPrice >= 0 && pricing.shippingPrice >= 0;
  });
  const downPaymentAmount = parseFloat(downPaymentAmountInput);
  const hasValidDownPayment =
    downPaymentAmountInput.trim() !== '' && Number.isFinite(downPaymentAmount) && downPaymentAmount >= 0;

  const parsedExtraLineItems = lineItemDrafts.flatMap((draft) => {
    const label = draft.label.trim();
    const amount = parseFloat(draft.amountInput);
    if (label !== '' && Number.isFinite(amount) && amount > 0) {
      return [{ label, amount, type: draft.type }];
    }
    return [];
  });
  const extraLineItemsTotal = parsedExtraLineItems.reduce(
    (sum, item) => sum + signedLineItemAmount(item.amount, item.type),
    0
  );
  const docFeePreview =
    documentMode === 'invoice_pdf' && includeDocFee
      ? RETAIL_ORDER_DOC_FEE * vehicles.length
      : 0;

  const pdfOnlyGrandTotal =
    vehicles.reduce((sum, vehicle) => {
      const pricing = pdfPricingByVehicleId[vehicle.id];
      return sum + (pricing?.listPrice ?? Number(vehicle.optic_list_price || 0)) + (pricing?.shippingPrice ?? 0);
    }, 0) +
    extraLineItemsTotal +
    docFeePreview;

  const showExtraLineItemFields =
    documentMode === 'retail_order' || documentMode === 'invoice_pdf';
  const lineItemDraftsInvalid = lineItemDrafts.some((draft) => {
    const label = draft.label.trim();
    const amountRaw = draft.amountInput.trim();
    if (label === '' && amountRaw === '') {
      return false;
    }
    if (label === '' || amountRaw === '') {
      return true;
    }
    const amount = parseFloat(amountRaw);
    return !Number.isFinite(amount) || amount <= 0;
  });

  const updateLineItemDraft = (
    id: string,
    patch: Partial<Pick<LineItemDraft, 'label' | 'amountInput' | 'type'>>
  ) => {
    setLineItemDrafts((prev) =>
      prev.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft))
    );
  };

  const removeLineItemDraft = (id: string) => {
    setLineItemDrafts((prev) => prev.filter((draft) => draft.id !== id));
  };

  const formatVehicleInfo = (vehicle: Vehicle, includeVin = true) => {
    const parts = [
      vehicle.year,
      vehicle.make,
      vehicle.model,
      includeVin && vehicle.vin && `VIN: ${vehicle.vin}`,
    ].filter(Boolean);
    return parts.join(' • ') || 'Unknown vehicle';
  };

  const modeDescription: Record<SaleDocumentMode, string> = {
    retail_order:
      'Assign banks or cash deals per truck. Generates PandaDoc retail order(s) and bank/cash PDF invoices as configured.',
    invoice_pdf:
      'Generates a PDF invoice with per-truck list and shipping prices. Retail order and bank assignment are skipped.',
    down_payment_pdf:
      'Generates a PDF listing trucks with a single down payment amount. No per-truck prices.',
  };

  const submitDisabled =
    loading ||
    !hasClientInfo ||
    (documentMode === 'retail_order' && (!allVehiclesAssigned || !hasAssignments)) ||
    (documentMode === 'invoice_pdf' && !hasPdfPricingValues) ||
    (documentMode === 'down_payment_pdf' && !hasValidDownPayment) ||
    lineItemDraftsInvalid;

  const submitLabel =
    loading
      ? 'Generating...'
      : documentMode === 'invoice_pdf'
        ? 'Generate PDF'
        : documentMode === 'down_payment_pdf'
          ? 'Generate down payment PDF'
          : `Generate Retail Order${cashDealVehicles.length > 0 || groupedByBank.length > 0 ? ' & Invoices' : ''}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Retail Orders</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a document type, confirm client details, then configure trucks for that workflow.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Client details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fields below are used in generated documents. Edit as needed before generating.
          </Typography>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                value={manualFirstName}
                onChange={(e) => setManualFirstName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={manualLastName}
                onChange={(e) => setManualLastName(e.target.value)}
                fullWidth
                required
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Company Name"
              value={manualCompany}
              onChange={(e) => setManualCompany(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Street Address"
              value={manualStreetAddress}
              onChange={(e) => setManualStreetAddress(e.target.value)}
              fullWidth
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="City"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="State"
                value={manualState}
                onChange={(e) => setManualState(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Zip Code"
                value={manualPostalCode}
                onChange={(e) => setManualPostalCode(e.target.value)}
                fullWidth
                required
              />
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Document type
          </Typography>
          <ToggleButtonGroup
            value={documentMode}
            exclusive
            onChange={(_, value: SaleDocumentMode | null) => {
              if (value) setDocumentMode(value);
            }}
            fullWidth
            sx={{ mb: 1 }}
          >
            <ToggleButton value="retail_order">Retail order</ToggleButton>
            <ToggleButton value="invoice_pdf">Invoice PDF</ToggleButton>
            <ToggleButton value="down_payment_pdf">Down payment PDF</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {modeDescription[documentMode]}
          </Typography>

          {showExtraLineItemFields && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Additional line items (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add custom charges (e.g. Warranty) or deposits. Charges add to the total; deposits
                subtract. Enter amounts as positive values.
              </Typography>
              <Stack spacing={1.5}>
                {lineItemDrafts.map((draft, index) => {
                  const label = draft.label.trim();
                  const amountRaw = draft.amountInput.trim();
                  const amount = parseFloat(amountRaw);
                  const isPartial =
                    (label !== '' && amountRaw === '') || (label === '' && amountRaw !== '');
                  const isInvalidAmount =
                    amountRaw !== '' && (!Number.isFinite(amount) || amount <= 0);
                  return (
                    <Stack
                      key={draft.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ sm: 'flex-start' }}
                    >
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={draft.type}
                        onChange={(_, value: ExtraLineItemType | null) => {
                          if (value) {
                            updateLineItemDraft(draft.id, { type: value });
                          }
                        }}
                        sx={{ mt: { xs: 0, sm: 1 }, flexShrink: 0 }}
                      >
                        <ToggleButton value="charge">Charge</ToggleButton>
                        <ToggleButton value="deposit">Deposit</ToggleButton>
                      </ToggleButtonGroup>
                      <TextField
                        label={`Line item ${index + 1}`}
                        value={draft.label}
                        onChange={(e) => updateLineItemDraft(draft.id, { label: e.target.value })}
                        placeholder={draft.type === 'deposit' ? 'Deposit' : 'Warranty'}
                        fullWidth
                        error={isPartial && label === ''}
                        helperText={
                          isPartial && label === '' ? 'Enter a label or clear the amount' : undefined
                        }
                      />
                      <TextField
                        label="Amount"
                        type="number"
                        value={draft.amountInput}
                        onChange={(e) => updateLineItemDraft(draft.id, { amountInput: e.target.value })}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="0.00"
                        fullWidth
                        error={isPartial || isInvalidAmount}
                        helperText={
                          isInvalidAmount
                            ? 'Amount must be greater than 0'
                            : isPartial && amountRaw === ''
                              ? 'Enter an amount or clear the label'
                              : undefined
                        }
                      />
                      <Button
                        onClick={() => removeLineItemDraft(draft.id)}
                        sx={{ mt: { xs: 0, sm: 1 }, minWidth: 88 }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  );
                })}
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setLineItemDrafts((prev) => [...prev, createEmptyLineItemDraft()])}
                  >
                    Add line item
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}

          {isPdfMode && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                PDF options
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideVins}
                      onChange={(e) => setHideVins(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Hide VINs on PDF
                      {hideVins && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          VIN column will be omitted from the generated PDF
                        </Typography>
                      )}
                    </Typography>
                  }
                />
                {documentMode === 'invoice_pdf' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeDocFee}
                        onChange={(e) => setIncludeDocFee(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Include doc fee
                        {includeDocFee && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Adds ${RETAIL_ORDER_DOC_FEE.toLocaleString('en-US')} × {vehicles.length} truck
                            {vehicles.length !== 1 ? 's' : ''} to the invoice total
                          </Typography>
                        )}
                      </Typography>
                    }
                  />
                )}
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
            <Typography variant="subtitle2" fontWeight="600">
              {documentMode === 'invoice_pdf'
                ? 'PDF Pricing'
                : documentMode === 'down_payment_pdf'
                  ? 'Trucks'
                  : 'Truck Package'}{' '}
              ({vehicles.length} truck{vehicles.length !== 1 ? 's' : ''})
            </Typography>
          </Paper>

          {documentMode === 'down_payment_pdf' && (
            <Stack spacing={2}>
              <TextField
                label="Down payment amount"
                type="number"
                value={downPaymentAmountInput}
                onChange={(e) => setDownPaymentAmountInput(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0.00"
                fullWidth
                required
              />
              {vehicles.map((vehicle, index) => (
                <Paper key={vehicle.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Truck {index + 1}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatVehicleInfo(vehicle, !hideVins)}
                  </Typography>
                  {vehicle.stock_number && (
                    <Typography variant="caption" color="text.secondary">
                      Stock: {vehicle.stock_number}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {documentMode === 'invoice_pdf' && (
            <Stack spacing={2}>
              {vehicles.map((vehicle, index) => {
                const pricing = pdfPricingByVehicleId[vehicle.id] || {
                  listPrice: Number(vehicle.optic_list_price || 0),
                  shippingPrice: 0,
                };
                const rowTotal = pricing.listPrice + pricing.shippingPrice;
                return (
                  <Paper key={vehicle.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Truck {index + 1}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatVehicleInfo(vehicle)}
                        </Typography>
                        {vehicle.stock_number && (
                          <Typography variant="caption" color="text.secondary">
                            Stock: {vehicle.stock_number}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="List Price"
                          type="number"
                          value={pricing.listPrice}
                          onChange={(e) => handlePdfListPriceChange(vehicle.id, parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                        <TextField
                          label="Shipping"
                          type="number"
                          value={pricing.shippingPrice}
                          onChange={(e) => handlePdfShippingChange(vehicle.id, parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                        <TextField
                          label="Row Total"
                          value={rowTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">
                  Grand Total: {pdfOnlyGrandTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
              </Paper>
            </Stack>
          )}

          {documentMode === 'retail_order' && (
            <>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={separateCustomerInvoices}
                      onChange={(e) => setSeparateCustomerInvoices(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Separate customer invoices
                      {separateCustomerInvoices && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Each truck will receive its own retail order document
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={generateOdometerStatements}
                      onChange={(e) => setGenerateOdometerStatements(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Generate odometer statements
                      {generateOdometerStatements && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Each truck will receive its own odometer statement document
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideVins}
                      onChange={(e) => setHideVins(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Hide VINs on PDF invoices
                      {hideVins && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          VIN column will be omitted from generated bank and cash-deal PDFs
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Paper>

              <Stack spacing={2}>
                {assignments.map((assignment, index) => (
                  <Paper key={assignment.vehicleId} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Truck {index + 1}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {formatVehicleInfo(assignment.vehicle)}
                          </Typography>
                          {assignment.vehicle.stock_number && (
                            <Typography variant="caption" color="text.secondary">
                              Stock: {assignment.vehicle.stock_number}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={assignment.isCashDeal}
                                onChange={(e) => handleCashDealChange(assignment.vehicleId, e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Cash Deal"
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 300 }}>
                          <Autocomplete<BankProfile, false, false, false>
                            options={salesBankProfiles}
                            getOptionLabel={(option) => option.bankName}
                            value={assignment.bankProfile}
                            onChange={(_, newValue) => {
                              handleBankChange(assignment.vehicleId, newValue);
                            }}
                            disabled={assignment.isCashDeal}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Bank Profile"
                                required={!assignment.isCashDeal}
                                placeholder={assignment.isCashDeal ? 'N/A (Cash Deal)' : 'Select a bank profile'}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {isLoadingBankProfiles && <CircularProgress size={20} />}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {option.bankName}
                                  </Typography>
                                  {option.bankContacts && option.bankContacts.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {option.bankContacts[0].email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            loading={isLoadingBankProfiles}
                            noOptionsText={
                              isLoadingBankProfiles
                                ? 'Loading bank profiles...'
                                : 'No sales bank profiles found'
                            }
                            filterOptions={(options, { inputValue }) => {
                              if (typeof inputValue === 'string' && inputValue.trim() === '') {
                                return options;
                              }

                              return options.filter((option) => {
                                const bankName = option.bankName.toLowerCase();
                                const searchTerm = inputValue.toLowerCase();

                                return (
                                  bankName.includes(searchTerm) ||
                                  option.bankContacts?.some(
                                    contact =>
                                      contact.email.toLowerCase().includes(searchTerm) ||
                                      contact.name.toLowerCase().includes(searchTerm)
                                  )
                                );
                              });
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }} />
                        <Box sx={{ flex: 1, minWidth: 200 }} />
                        <Box sx={{ flex: 1, minWidth: 300 }}>
                          <TextField
                            label="Shipping Price"
                            type="number"
                            value={assignment.shippingPriceInput}
                            onChange={(e) => {
                              handleShippingPriceChange(assignment.vehicleId, e.target.value);
                            }}
                            inputProps={{
                              min: 0,
                              step: 0.01,
                            }}
                            placeholder="0.00"
                            fullWidth
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitDisabled}
          variant="contained"
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleInvoiceDialog;
